import { zPedidoDeEnvioDeAnexo } from '@arena/shared/schemas';
import { comBase, mensagemDoErro } from '../api/cliente.js';
import { financeiroApi } from '../api/financeiro.js';

/**
 * O comprovante, do lado do navegador — usado pela ficha do lançamento
 * (`AnexosDoLancamento`): anexar depois, abrir, tirar.
 *
 * O arquivo vai direto do navegador para o Storage, nunca pelo backend
 * Node: o servidor só decide (URL assinada) e confere (o objeto no
 * Storage) antes de gravar o anexo. `comBase()` resolve a URL de envio para
 * o mesmo prefixo que `api()` usa — ver o comentário em `api/cliente.ts`.
 *
 * O envio do comprovante que nasce JUNTO com o lançamento é o de
 * `ModalDeLancamento` (`enviarAnexoSeHouver`), com a mesma sequência de três
 * passos: ele ficou como estava de propósito, para o fluxo de criar não
 * mudar nesta tarefa.
 */

/** Falha cuja mensagem já é para a pessoa ler — as demais passam por `mensagemDoErro`. */
export class ErroDeComprovante extends Error {
  constructor(mensagem: string) {
    super(mensagem);
    this.name = 'ErroDeComprovante';
  }
}

/** A frase de uma falha de envio ou de abertura, qualquer que tenha sido o passo. */
export function mensagemDoComprovante(erro: unknown): string {
  return erro instanceof ErroDeComprovante ? erro.message : mensagemDoErro(erro);
}

/**
 * Os três passos: pede o destino, envia o arquivo, confirma. Nada é
 * gravado até o último; se algum falhar, o anexo simplesmente não existe.
 *
 * Tipo, tamanho e nome são conferidos antes pelo MESMO schema que o servidor
 * usa: "O arquivo passa de 10 MB." chega antes de o envio gastar a rede da
 * quadra, e o servidor confere de novo o que realmente chegou ao Storage.
 */
export async function enviarComprovante(lancamentoId: string, arquivo: File): Promise<void> {
  const pedido = zPedidoDeEnvioDeAnexo.safeParse({
    nome: arquivo.name,
    tipo: arquivo.type,
    tamanho: arquivo.size,
  });
  if (!pedido.success) {
    throw new ErroDeComprovante(
      pedido.error.issues[0]?.message ?? 'Este arquivo não pode ser anexado.',
    );
  }

  const envio = await financeiroApi.pedirEnvioDeAnexo(lancamentoId, pedido.data);
  // Fetch CRU: SEM api(), que adicionaria Authorization e
  // content-type: application/json — os dois errados aqui.
  const resposta = await fetch(comBase(envio.url), {
    method: envio.metodo,
    headers: envio.cabecalhos,
    body: arquivo,
  });
  if (!resposta.ok) throw new ErroDeComprovante('Falha no envio do arquivo.');
  await financeiroApi.confirmarAnexo(lancamentoId, { anexoId: envio.anexoId, nome: arquivo.name });
}

/**
 * Abre o comprovante numa aba nova, por URL assinada que expira em
 * minutos.
 *
 * A aba abre ANTES de a URL chegar: o navegador só deixa abrir janela a
 * partir do clique, e a URL ainda vai demorar uma ida ao servidor — o
 * bloqueador de pop-up pega o `window.open` que vem depois do `await`. A
 * aba precisa do vínculo para ser navegada depois (por isso não é
 * `noopener` na abertura), e o `opener` é cortado à mão logo em seguida.
 */
export async function abrirComprovante(anexoId: string): Promise<void> {
  const aba = window.open('about:blank', '_blank');
  if (!aba) {
    throw new ErroDeComprovante('O navegador bloqueou a nova aba. Libere os pop-ups deste site.');
  }
  aba.opener = null;
  try {
    const { url } = await financeiroApi.urlDoAnexo(anexoId);
    aba.location.href = url;
  } catch (erro) {
    aba.close();
    throw erro;
  }
}
